
import {filter,  debounceTime } from 'rxjs/operators';
import { Subscription ,  fromEvent } from 'rxjs';
import { Store } from '@ngrx/store';
import { Directive, ElementRef, Input, AfterViewInit, OnDestroy} from '@angular/core';
import { AppState } from '../../interfaces/app-state.interface';
import { User } from '../../models/User';

enum DispatchersAliasEnum {
    Segment = 'seg',
    Mixpanel = 'mxp',
    Analytics = 'ga',
    TagManager = 'gtm',
}

type TrackingDispatchers = DispatchersAliasEnum[];

interface ITrackingOptions {
    value?: any;
    label: string;
    events: string[];
    category: string;
    dispatchers: TrackingDispatchers;
}

interface ITrackingEvent {
    label: string;
    value: string;
    action: string;
    category: string;
}

@Directive({
  selector: '[tracking]',
})
export class TrackingDirective implements AfterViewInit, OnDestroy {

    // Configuração de trackeamento. Ex: ['Categoria', 'nome-do-elemento-ou-label', ['click', 'load', 'etc...'], ['ga', 'mxp'], 'my-value']
    @Input('tracking')
    public config: [string, string, string[], TrackingDispatchers, any?];

    // Url do elemento, onde o evento aconteceu
    public elementUrl: string;

    // Times default em ms para destruir observable de eventos e para disparo de eventos
    public static DESTROY_EVENTS_TIMEOUT = 2000;
    public static DISPATCH_EVENTS_DEBOUNCETIME = 1000;

    // Dados do usuários, se logado
    private user: User & {cblc: string, id: string};

    // Subscriptiosn a srem destruídas junto com a diretiva
    private userSubscription$: Subscription;
    private eventsSubscriptions$: Subscription[] = [];

    constructor(
        private element: ElementRef<HTMLElement>,
        store: Store<AppState>,
    ) {

        // Dados do usuário logado
        this.userSubscription$ = store
            .select((state) => ({...state.userProfile.user, ...state.authentication.currentUser})).pipe(
            filter((user) => !!user.id))
            .subscribe((user) => this.user = user);

    }

    public ngAfterViewInit() {

        this.elementUrl = window.location.href;
        const { category, dispatchers, events } = this.options;

        // Não há disparadores para o evento, nada a fazer
        if (
            !category
            || !dispatchers
            || !Object
                .keys(DispatchersAliasEnum)
                .some((alias) => dispatchers.includes(DispatchersAliasEnum[alias]))
        ) return;

        // Ouvir todos os eventos do elemento a serem trackeados
        if (Array.isArray(events)) events.forEach((event: string) => this.listenEvent(event));

    }

    public ngOnDestroy() {

        // Aguardar para unsubscribe pois os eventos precisam ser eneviados mesmo que o componente seja destruído
        setTimeout(
            () => {

                // Se desincrever dos observables de eventos
                this.eventsSubscriptions$.forEach((subscription) => subscription.unsubscribe());

                // Se desisncrever do observable de dados do usuários
                if (this.userSubscription$) this.userSubscription$.unsubscribe();

            },
            TrackingDirective.DESTROY_EVENTS_TIMEOUT,
        );

    }

    /**
     * Gerar event listeners para os eventos a serem trackeados no elemento
     *
     * @param event
     */
    private listenEvent(event: string) {

        const { label } = this.options;

        // TRacking de carregamento é disparado sem listenner, pois o elemento já carregou
        if (event === 'load') return this.trackEvent(label, event);

        // Demais eventos são escutados, e quando ocorrerem seu trackeamento será feito
        this.eventsSubscriptions$.push(
            fromEvent(this.element.nativeElement, event)
                .pipe(debounceTime(TrackingDirective.DISPATCH_EVENTS_DEBOUNCETIME))
                .subscribe(() => this.trackEvent(label, event)),
        );

    }

    /**
     * Trackear um evento definido através dos métodos de serviços correspondentes
     *
     * @param label
     * @param event
     */
    private trackEvent(label: string, event: string) {

        const { category, value, dispatchers } = this.options;

        // Disparar eventos de trackeamento em cada serviço correspondente
        dispatchers.forEach((dispatcher) => this.dispatch(dispatcher, {
            label,
            value,
            category,
            action: event,
        }));

    }

    /**
     * Função geral para dispatch de eventos
     *
     * @param dispatcherAlias - Alias da função a ser chamada para dispatch
     * @param event - evento a ser trackeado
     */
    private dispatch(dispatcherAlias: DispatchersAliasEnum, event: ITrackingEvent) {
        switch (dispatcherAlias) {
            case DispatchersAliasEnum.Segment: return this.dispatcherSegment(event);
            case DispatchersAliasEnum.Mixpanel: return this.dispatcherMixpanel(event);
            case DispatchersAliasEnum.Analytics: return this.dispatcherGoogleAnalytics(event);
            case DispatchersAliasEnum.TagManager: return this.dispatcherGoogleTagManager(event);
            // tslint:disable-next-line:no-unused-expression
            default: () => null;
        }
    }

    /**
     * Enviar um evento trackeado para o Google Analytics
     *
     * @param event
     */
    private dispatcherGoogleAnalytics(event: ITrackingEvent) {

        if (!(window as any).ga) return;

        (window as any).ga('send', 'event', {
            url: this.elementUrl,
            eventLabel: event.label,
            eventValue: event.value,
            eventAction: event.action,
            eventCategory: event.category,
        });

    }

    /**
     * TODO: Enviar um evento trackeado para o Google Tag Manager
     *
     * @param event
     */
    private dispatcherGoogleTagManager(event: ITrackingEvent) {
        // console.log('>>> [GTM]', event);
    }

    /**
     * TODO: Enviar um evento trackeado para o Mixpanel
     *
     * @param event
     */
    private dispatcherMixpanel(event: ITrackingEvent) {
        // console.log('>>> [MIXPANEL]', event);
    }

    /**
     * TODO: Enviar um evento trackeado para o Segment
     *
     * @param event
     */
    private dispatcherSegment(event: ITrackingEvent) {
        // console.log('>>> [MIXPANEL]', event);
    }

    /**
     * Retornar um objeto com os dados definidos na configuração da diretiva no elemento
     */
    private get options(): ITrackingOptions {

        // Carrega dados configurados na diretiva
        const [category, label, events, dispatchers, value] = this.config;

        // Retorna objeto de opções
        return {

            // Valor a ser trackeado se houver: 'my-value'
            value,

            // Array dos eventos a serem escutados: ['click', 'mouseover'] etc...
            events,

            // Categoria do evento 'Meus Eventos'
            category,

            // Label identificador do elemento a ser trackeado
            label,

            // Id do elemento para trackeamento: 'my-element-input'
            dispatchers,

        };

    }
}
